const PrivacyPolicy: React.FC = () => (
  <div className="container py-5 privacy-main">
    <h2>Privacy Policy</h2>
    <p>
      JJ’S AHA LLC ("AMOUR", "We", "Our", "Us") values your privacy and is
      committed to protecting your personal information. This Privacy Policy
      explains how we collect, use, and protect your data when you use our
      services via the AMOUR app and website. By using our app, you consent to
      the practices described in this Privacy Policy.
    </p>

    <p><strong>1. Information We Collect About You</strong></p>

    <p>
      We collect various types of personal data to enhance your experience with
      AMOUR. The following data may be collected:
    </p>

    <ul>
      <li>
        <strong>Contact Data:</strong> Name, email address, postal address,
        phone number, and social network details.
      </li>
      <li>
        <strong>Account Login Data:</strong> Login ID/email, screen name,
        password (in unrecoverable form), and security questions.
      </li>
      <li>
        <strong>Device Data:</strong> IP address, device type, operating
        system, unique device identifiers (e.g., mobile ID, advertising ID).
      </li>
      <li>
        <strong>Demographic Data:</strong> Age, gender, geographic location
        (ZIP code), preferences, and other behavioral data such as hobbies and
        lifestyle.
      </li>
      <li>
        <strong>Third-Party Social Network Data:</strong> Data from your
        interactions with social networks like Facebook or Instagram (e.g.,
        name, profile picture).
      </li>
      <li>
        <strong>Site Usage Data:</strong> Interaction data such as page views,
        clicks, and time spent on our app or website.
      </li>
      <li>
        <strong>Geolocation Data:</strong> Location information collected with
        your consent.
      </li>
      <li>
        <strong>Feedback Data:</strong> User-generated content shared with us,
        such as photos, videos, or testimonials.
      </li>
    </ul>

    <p><strong>2. How We Collect Your Information</strong></p>

    <ul>
      <li>
        <strong>Directly from You:</strong> When you register, use our
        services, fill out forms, or communicate with us.
      </li>
      <li>
        <strong>Automatically:</strong> Through cookies, web beacons, and
        tracking technologies when you use our app or website.
      </li>
      <li>
        <strong>From Third-Party Sources:</strong> We may receive data from our
        business partners, social networks, and affiliates.
      </li>
    </ul>

    <p><strong>3. Personal Data of Children</strong></p>

    <p>
      AMOUR does not knowingly collect personal data from children under the age
      of 18. If we learn that a child under 18 has provided us with personal
      data, we will take immediate steps to delete such data. Parents or
      guardians who believe their child has provided data may contact us at
      <a href="mailto:support@amourapp.com">support@amourapp.com</a>.
    </p>

    <p><strong>4. Child Safety Standards &amp; CSAE/CSAM Prevention Policy</strong></p>

    <p>
      AMOUR, operated by JJ’S AHA LLC, is dedicated to ensuring the safety of
      all users, particularly children. We strictly prohibit the creation,
      sharing, or possession of Child Sexual Abuse and Exploitation (CSAE) and
      Child Sexual Abuse Material (CSAM) on our platform.
    </p>

    <h4>
      1. Zero-Tolerance Policy
    </h4>
    <ul>
      <li>
        AMOUR maintains a zero-tolerance policy towards any form of CSAE or
        CSAM. Any violation of this policy will result in immediate account
        suspension or termination.
      </li>
      <li>
        Any content involving child exploitation or harm will be removed
        immediately, and we will cooperate fully with law enforcement
        authorities.
      </li>
    </ul>

    <h4>
      2. Moderation and Detection
    </h4>
    <ul>
      <li>
        We use automated tools and manual moderation to monitor content for
        CSAE/CSAM.
      </li>
      <li>
        All user-generated content (photos, messages, videos) is subject to
        review for harmful content.
      </li>
      <li>
        Users can report suspicious content directly via the app or by emailing
        <a href="mailto:amourdatingapp@gmail.com">amourdatingapp@gmail.com</a>.
      </li>
    </ul>

    <h4>
      3. Enforcement and Reporting
    </h4>
    <ul>
      <li>
        Violations of our CSAE/CSAM policy result in account termination, and
        such behavior is reported to appropriate authorities.
      </li>
      <li>
        We cooperate with law enforcement and comply with legal requests for
        information and evidence related to CSAE/CSAM.
      </li>
    </ul>

    <h4>
      4. Reporting Violations
    </h4>
    <p>
      If you encounter any content that violates our CSAE/CSAM policies, please
      report it immediately by:
    </p>
    <ul>
      <li>Using the in-app reporting feature; or</li>
      <li>
        Emailing us at
        <a href="mailto:amourdatingapp@gmail.com">amourdatingapp@gmail.com</a>.
      </li>
    </ul>
    <p>
      AMOUR will investigate all reports promptly and take appropriate action.
    </p>

    <p><strong>5. How We Use Your Personal Data</strong></p>

    <p>We use your personal data for the following purposes:</p>

    <ul>
      <li>
        <strong>Consumer Service:</strong> Responding to inquiries, complaints,
        and account support.
      </li>
      <li>
        <strong>Marketing &amp; Promotions:</strong> Sending offers, updates, and
        communications related to our products and services. You can opt out of
        marketing emails at any time.
      </li>
      <li>
        <strong>Third-Party Social Networks:</strong> Interacting with you
        through features like social logins, likes, and other integrations.
      </li>
      <li>
        <strong>Research &amp; Analytics:</strong> Analyzing usage trends,
        improving our app and services, and optimizing marketing efforts.
      </li>
    </ul>

    <p><strong>6. Data Protection and Retention</strong></p>

    <p>
      AMOUR implements standard security measures to protect your personal data.
      However, no method of data transmission over the internet is completely
      secure. We take reasonable steps to safeguard your data but cannot
      guarantee its absolute security.
    </p>
    <p>
      We retain personal data for as long as necessary for the purposes outlined
      in this Privacy Policy or as required by law.
    </p>

    <p><strong>7. How to Delete Your Account</strong></p>

    <p>
      You can delete your account by navigating to Settings and selecting Delete
      Account. This will log you out and remove your data from our system within
      30 days. If you change your mind, email us at
      <a href="mailto:amourdatingapp@gmail.com">amourdatingapp@gmail.com</a>
      within 30 days to request account reinstatement.
    </p>

    <h4>8. Your Choices About How We Use and Disclose Your Personal Data</h4>

    <p>
      You have control over your personal data, and the following choices are
      available:
    </p>

    <ul>
      <li>
        <strong>Opt-Out of Marketing Communications:</strong> You can opt out of
        marketing communications at any time by following the instructions in
        our emails.
      </li>
      <li>
        <strong>Access and Deletion Requests:</strong> You can request access to
        the personal data we have collected about you or request its deletion.
        Please contact us at
        <a href="mailto:amourdatingapp@gmail.com">amourdatingapp@gmail.com</a>
        for these requests.
      </li>
      <li>
        <strong>Cookies and Tracking:</strong> You can disable cookies through
        your browser settings or use other tools to control tracking
        technologies.
      </li>
    </ul>

    <p><strong>9. Third-Party Websites and Services</strong></p>

    <p>
      Our app and website may link to third-party websites or services. We are
      not responsible for the privacy practices of these external sites. We
      recommend reviewing their privacy policies before providing any personal
      information.
    </p>

    <p><strong>10. Your California Privacy Rights</strong></p>

    <p>California residents have the right to:</p>

    <ul>
      <li>
        <strong>Request Information:</strong> You can request details about the
        personal data we have collected, shared, and sold, up to twice a year.
      </li>
      <li>
        <strong>Request Deletion:</strong> You can request that we delete your
        personal data.
      </li>
      <li>
        <strong>Opt-Out of Sales:</strong> You can opt out of the sale of your
        personal data, if applicable.
      </li>
    </ul>

    <p>
      For more information or to make a request, email us at
      <a href="mailto:amourdatingapp@gmail.com">amourdatingapp@gmail.com</a>.
    </p>

    <p><strong>11. Changes to This Privacy Policy</strong></p>

    <p>
      AMOUR reserves the right to update this Privacy Policy as needed. We will
      notify users of significant changes, and the updated policy will be posted
      on this page with an updated effective date.
    </p>

    <p><strong>12. Contact Us</strong></p>

    <p>
      If you have any questions or concerns about this Privacy Policy or our
      data practices, please contact us at:
    </p>
    <p>
      Email:
      <a href="mailto:amourdatingapp@gmail.com">amourdatingapp@gmail.com</a>
    </p>
  </div>
);

export default PrivacyPolicy;
